import type {
  LoginRequestDto,
  MerchantAuthResponseDto,
  BackofficeAuthResponseDto,
} from '@kardume/types';
import api, { extractErrorMessage } from './http';
import { session, setAppNamespace, getNamespace } from './session';
import type { AuthNamespace } from './session';

interface ChangePasswordPayload {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Authentication for both apps.
 * - `admin`      -> merchant panel  (POST /v1/merchant/auth/login)
 * - `backoffice` -> internal panel  (POST /v1/backoffice/auth/login)
 */
export const authService = {
  setAppNamespace,
  getNamespace,

  async login(credentials: LoginRequestDto): Promise<MerchantAuthResponseDto> {
    try {
      const { data } = await api.post<MerchantAuthResponseDto>('/v1/merchant/auth/login', credentials);
      if (data.authStatus === 'OK' && data.accessToken && data.refreshToken) {
        session.set(data.accessToken, data.refreshToken, data.user, 'admin');
      }
      return data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Não foi possível entrar. Confira seu e-mail e senha.'));
    }
  },

  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    try {
      await api.post('/v1/merchant/auth/change-password', payload);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Não foi possível alterar a senha.'));
    }
  },

  async forgotPassword(email: string): Promise<void> {
    try {
      await api.post('/v1/merchant/auth/forgot-password', { email });
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Não foi possível iniciar a redefinição de senha.'));
    }
  },

  async backofficeLogin(credentials: LoginRequestDto): Promise<BackofficeAuthResponseDto> {
    try {
      const { data } = await api.post<BackofficeAuthResponseDto>('/v1/backoffice/auth/login', credentials);
      if (data.authStatus === 'OK' && data.accessToken && data.refreshToken) {
        session.set(data.accessToken, data.refreshToken, data.user, 'backoffice');
      }
      return data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Não foi possível entrar no Backoffice. Confira seu e-mail e senha.'));
    }
  },

  /**
   * "Esqueci minha senha" do backoffice. A resposta é sempre a mesma, exista ou
   * não a conta - quem não tem acesso não descobre aqui quem tem.
   */
  async backofficeForgotPassword(email: string): Promise<void> {
    try {
      await api.post('/v1/backoffice/auth/forgot-password', { email });
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Não foi possível iniciar a redefinição de senha.'));
    }
  },

  async backofficeChangePassword(payload: ChangePasswordPayload): Promise<void> {
    try {
      await api.post('/v1/backoffice/auth/change-password', payload);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Não foi possível alterar a senha.'));
    }
  },

  logout(namespace?: AuthNamespace): void {
    session.clear(namespace);
  },

  getUser<T = unknown>(): T | null {
    return session.getUser<T>();
  },

  updateUser(user: unknown): void {
    session.updateUser(user);
  },

  isAuthenticated(): boolean {
    return session.isAuthenticated();
  },

  getToken(): string | null {
    return session.getToken();
  },
};
